import CheckIcon from "@mui/icons-material/Check"
import InsertLinkIcon from "@mui/icons-material/InsertLink"
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined"
import { Box, Button, Typography } from "@mui/material"
import React, { useState } from "react"
import { usePhoneScreen } from "@app/core/hooks/use-phone-screen"
import { continueOnPhone, isContinuingOnPhone } from "@app/core/services/phone-screen-service"
import { customPalette } from "../../../theme"
import Right from "../../../assets/Right Background.png"
import ReactLogoTTB from "../../../assets/LogoTTB_green.svg"

/**
 * Shows the app, or, on a phone, a screen explaining that the app is made for a computer. The user may still go on,
 * for the rest of the browser session. Placed above the contexts, so that nothing is loaded behind the screen.
 */
interface IProps {
  children: React.JSX.Element
}

export function PhoneScreenGuard({ children }: Readonly<IProps>): React.JSX.Element {
  const phoneScreen = usePhoneScreen()
  const [continuing, setContinuing] = useState<boolean>(isContinuingOnPhone)

  if (!phoneScreen || continuing) {
    return children
  }

  return (
    <PhoneScreen
      onContinue={() => {
        continueOnPhone()
        setContinuing(true)
      }}
    />
  )
}

function PhoneScreen({ onContinue }: Readonly<{ onContinue(): void }>): React.JSX.Element {
  const [linkCopied, setLinkCopied] = useState<boolean>(false)

  function copyLink(): void {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => setLinkCopied(true))
      .catch((error) => console.error("Failed to copy the link: ", error))
  }

  return (
    <Box
      component="main"
      sx={{
        position: "relative",
        minHeight: "100dvh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        p: "32px 24px",
        textAlign: "center",
        backgroundColor: customPalette.dark1,
        color: customPalette.white,
      }}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${Right})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.9,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: "-20%",
          right: "-30%",
          width: "90%",
          height: "60%",
          background: "radial-gradient(ellipse at center, rgba(11,226,160,0.22) 0%, transparent 65%)",
          filter: "blur(50px)",
        }}
      />

      <Box component="img" src={ReactLogoTTB} alt="Time To Beem" sx={{ position: "relative", width: 150 }} />

      <Box
        sx={{
          position: "relative",
          width: 88,
          height: 88,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `2px solid ${customPalette.mainGreen5}`,
          backgroundColor: "rgba(11,226,160,0.08)",
          color: customPalette.mainGreen5,
        }}>
        <LaptopMacOutlinedIcon sx={{ fontSize: 44 }} />
      </Box>

      <Box sx={{ position: "relative", maxWidth: "34ch" }}>
        <Typography component="h1" sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.02em" }}>
          Time To Beem s'utilise sur ordinateur
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: 15, lineHeight: 1.6, color: "rgba(255,255,255,0.72)" }}>
          Maquettes 3D, saisie des bâtiments, tableaux de bord : nos outils ont besoin d'un grand écran. Ouvrez Time To Beem
          sur un ordinateur, ou à défaut sur une tablette.
        </Typography>
      </Box>

      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 1.5, width: "100%", maxWidth: 320 }}>
        <Button
          variant="contained"
          onClick={copyLink}
          startIcon={linkCopied ? <CheckIcon /> : <InsertLinkIcon />}
          sx={{
            height: 46,
            borderRadius: 3,
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            color: customPalette.dark1,
            backgroundColor: customPalette.mainGreen5,
            boxShadow: "0 6px 18px -2px rgba(11,226,160,0.45)",
            "&:hover": { backgroundColor: customPalette.mainGreen5 },
          }}>
          {linkCopied ? "Lien copié" : "Copier le lien pour l'ouvrir ailleurs"}
        </Button>
        <Button
          variant="text"
          onClick={onContinue}
          sx={{ textTransform: "none", fontSize: 14, color: "rgba(255,255,255,0.72)", textDecoration: "underline" }}>
          Continuer quand même
        </Button>
      </Box>
    </Box>
  )
}
